import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Plus, Edit, Trash2, Save, X } from 'lucide-react';
import BottomNavigation from '../../components/common/BottomNavigation';
import { useApp } from '../../context/AppContext';
import { useData } from '../../context/DataContext';
import { mockCategories } from '../../data/mockData';
import { FirestoreService } from '../../firebase/firestore';

const AdminCategory: React.FC = () => {
  const { setCurrentScreen } = useApp();
  const { categories, updateCategories } = useData();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: ''
  });

  // Use categories from context, fallback to mockCategories if empty
  const availableCategories = categories.length > 0 ? categories : mockCategories;

  const handleBack = () => {
    setCurrentScreen('admin-dashboard');
  };

  const handleAddCategory = async () => {
    if (!newCategory.name || !newCategory.description) {
      alert('Please fill in all required fields');
      return;
    }

    const category = {
      name: newCategory.name,
      description: newCategory.description,
      icon: '📦' // Default icon
    };

    try {
      console.log('💾 Saving category to Firestore...', category);
      const categoryId = await FirestoreService.addCategory(category);
      
      if (categoryId) {
        console.log('✅ Category saved to Firestore with ID:', categoryId);
        // Add the category with the Firestore ID to local state
        const categoryWithId = { ...category, id: categoryId };
        updateCategories([...availableCategories, categoryWithId]);
        setNewCategory({ name: '', description: '' });
        setShowAddForm(false);
        alert('Category added successfully!');
      } else {
        console.error('❌ Failed to save category to Firestore');
        alert('Failed to save category. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error saving category:', error);
      alert('Error saving category. Please try again.');
    }
  };

  const handleEditCategory = (category: any) => {
    setEditingCategory(category);
    setNewCategory({
      name: category.name,
      description: category.description
    });
  };

  const handleUpdateCategory = async () => {
    if (!newCategory.name || !newCategory.description) {
      alert('Please fill in all required fields');
      return;
    }

    const updatedCategory = {
      name: newCategory.name,
      description: newCategory.description,
      icon: editingCategory.icon || '📦'
    };

    try {
      console.log('💾 Updating category in Firestore...', updatedCategory);
      const success = await FirestoreService.updateCategory(editingCategory.id, updatedCategory);
      
      if (success) {
        console.log('✅ Category updated in Firestore');
        const updatedCategories = availableCategories.map(cat =>
          cat.id === editingCategory.id
            ? { ...cat, ...updatedCategory }
            : cat
        );
        updateCategories(updatedCategories);
        setEditingCategory(null);
        setNewCategory({ name: '', description: '' });
        alert('Category updated successfully!');
      } else {
        console.error('❌ Failed to update category in Firestore');
        alert('Failed to update category. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error updating category:', error);
      alert('Error updating category. Please try again.');
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        console.log('🗑️ Deleting category from Firestore...', categoryId);
        const success = await FirestoreService.deleteCategory(categoryId);
        
        if (success) {
          console.log('✅ Category deleted from Firestore');
          updateCategories(availableCategories.filter(cat => cat.id !== categoryId));
          alert('Category deleted successfully!');
        } else {
          console.error('❌ Failed to delete category from Firestore');
          alert('Failed to delete category. Please try again.');
        }
      } catch (error) {
        console.error('❌ Error deleting category:', error);
        alert('Error deleting category. Please try again.');
      }
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingCategory(null);
    setNewCategory({ name: '', description: '' });
  };

  const CategoryItem: React.FC<{ category: any; index: number }> = ({ category, index }) => (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.1 }}
      className="flex items-center justify-between p-4 bg-slate-800 rounded-lg border border-slate-700"
    >
      <div className="flex items-center space-x-3">
        <div className="text-2xl">{category.icon}</div>
        <div>
          <h3 className="text-white font-medium">{category.name}</h3>
          <p className="text-slate-400 text-sm">{category.description}</p>
        </div>
      </div>
      <div className="flex items-center space-x-2">
        <motion.button
          onClick={() => handleEditCategory(category)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="p-2 text-blue-400 hover:text-blue-300 hover:bg-slate-700 rounded-lg transition-colors duration-200"
        >
          <Edit size={16} />
        </motion.button>
        <motion.button
          onClick={() => handleDeleteCategory(category.id)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="p-2 text-red-400 hover:text-red-300 hover:bg-slate-700 rounded-lg transition-colors duration-200"
        >
          <Trash2 size={16} />
        </motion.button>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-slate-900 pb-20">
      {/* Header */}
      <div className="flex items-center p-6 bg-slate-800 border-b border-slate-700">
        <button
          onClick={handleBack}
          className="p-2 text-slate-400 hover:text-white mr-4"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-xl font-bold text-white">Category Management</h1>
      </div>

      <div className="p-6 space-y-6">
        {/* Add/Edit Category Form */}
        {(showAddForm || editingCategory) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-800 rounded-xl p-6 border border-slate-700"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">
                {editingCategory ? 'Edit Category' : 'Add New Category'}
              </h2>
              <button
                onClick={handleCancel}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors duration-200"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Category Name *
                </label>
                <input
                  type="text"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g., Construction Materials"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Description *
                </label>
                <input
                  type="text"
                  value={newCategory.description}
                  onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g., Cement"
                />
              </div>


              <div className="flex space-x-3">
                <motion.button
                  onClick={editingCategory ? handleUpdateCategory : handleAddCategory}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex-1 bg-teal-500 hover:bg-teal-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  <Save size={20} />
                  <span>{editingCategory ? 'Update Category' : 'Add Category'}</span>
                </motion.button>
                <motion.button
                  onClick={handleCancel}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-6 bg-slate-600 hover:bg-slate-700 text-white font-semibold py-3 rounded-lg transition-colors duration-200"
                >
                  Cancel
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Categories List */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">
              Categories ({availableCategories.length})
            </h2>
            {!showAddForm && !editingCategory && (
              <motion.button
                onClick={() => setShowAddForm(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2"
              >
                <Plus size={20} />
                <span>Add Category</span>
              </motion.button>
            )}
          </div>

          <div className="space-y-4">
            {availableCategories.length > 0 ? (
              availableCategories.map((category, index) => (
                <CategoryItem key={category.id} category={category} index={index} />
              ))
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">📁</span>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">No Categories</h3>
                <p className="text-slate-400 text-sm mb-4">
                  Get started by adding your first category
                </p>
                <motion.button
                  onClick={() => setShowAddForm(true)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-lg transition-colors duration-200"
                >
                  Add First Category
                </motion.button>
              </div>
            )}
          </div>
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default AdminCategory;
